/**
 * Configuration options for exponential backoff retry logic
 */
export interface ExponentialBackoffOptions {
  /** Multiplier for each subsequent retry (default: 2) */
  backoffFactor?: number;
  /** Initial delay in milliseconds (default: 1000) */
  baseDelay: number;
  /** Add random jitter to prevent thundering herd (default: true) */
  jitter?: boolean;
  /** Maximum delay between retries in milliseconds (default: 30000) */
  maxDelay?: number;
  /** Maximum number of retry attempts (default: 3) */
  maxRetries: number;
  /** Callback function called on each retry attempt */
  onRetry?: (error: unknown, attempt: number, delay: number) => void;
  /** Custom function to determine if an error should trigger a retry */
  shouldRetry?: (error: unknown, attempt: number) => boolean;
}

/**
 * Error thrown when all retry attempts are exhausted
 */
export class ExponentialBackoffError extends Error {
  public readonly attempts: number;
  public readonly lastError: unknown;

  constructor(attempts: number, lastError: unknown) {
    super(`All ${attempts} retry attempts exhausted`);
    this.name = "ExponentialBackoffError";
    this.attempts = attempts;
    this.lastError = lastError;
  }
}

/**
 * Default options for exponential backoff
 */
const DEFAULT_OPTIONS: Required<Omit<ExponentialBackoffOptions, "onRetry" | "shouldRetry">> = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 30000,
  backoffFactor: 2,
  jitter: true,
};

/**
 * Calculate delay with exponential backoff and optional jitter
 */
function calculateDelay(
  attempt: number,
  baseDelay: number,
  backoffFactor: number,
  maxDelay: number,
  jitter: boolean,
): number {
  const exponentialDelay = baseDelay * Math.pow(backoffFactor, attempt - 1);
  const delay = Math.min(exponentialDelay, maxDelay);

  if (jitter) {
    // Add random jitter of ±25% to prevent thundering herd
    const jitterRange = delay * 0.25;
    const randomJitter = (Math.random() - 0.5) * 2 * jitterRange;
    return Math.max(0, delay + randomJitter);
  }

  return delay;
}

/**
 * Sleep for a specified number of milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Execute an async function with exponential backoff retry logic
 *
 * @param fn - The async function to execute
 * @param options - Configuration options for retry behavior
 * @returns Promise that resolves to the function result or rejects after all retries
 *
 * @example
 * ```typescript
 * // Basic usage
 * const result = await exponentialBackoff(
 *   () => fetch('/api/data').then(res => res.json()),
 *   { maxRetries: 3, baseDelay: 1000 }
 * );
 *
 * // With custom retry logic
 * const result = await exponentialBackoff(
 *   () => apiCall(),
 *   {
 *     maxRetries: 5,
 *     baseDelay: 500,
 *     shouldRetry: (error, attempt) => {
 *       // Only retry on network errors, not 4xx errors
 *       return error instanceof NetworkError && attempt < 5;
 *     },
 *     onRetry: (error, attempt, delay) => {
 *       logger.info(`Retry ${attempt} after ${delay}ms due to:`, error);
 *     }
 *   }
 * );
 * ```
 */
export async function exponentialBackoff<T>(fn: () => Promise<T>, options: ExponentialBackoffOptions): Promise<T> {
  const {
    maxRetries,
    baseDelay,
    maxDelay = DEFAULT_OPTIONS.maxDelay,
    backoffFactor = DEFAULT_OPTIONS.backoffFactor,
    jitter = DEFAULT_OPTIONS.jitter,
    shouldRetry,
    onRetry,
  } = { ...DEFAULT_OPTIONS, ...options };

  // Default retry condition that uses the actual maxRetries value
  const defaultShouldRetry = (error: unknown, attempt: number): boolean => {
    return attempt <= maxRetries;
  };

  const retryShouldRetry = shouldRetry ?? defaultShouldRetry;

  let lastError: unknown;

  for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Don't retry if we've exceeded max attempts or if shouldRetry returns false
      if (attempt > maxRetries || !retryShouldRetry(error, attempt)) {
        throw new ExponentialBackoffError(attempt, error);
      }

      const delay = calculateDelay(attempt, baseDelay, backoffFactor, maxDelay, jitter);

      // Call retry callback if provided
      onRetry?.(error, attempt, delay);

      // Wait before retrying
      await sleep(delay);
    }
  }

  // This should never be reached, but TypeScript requires it
  throw new ExponentialBackoffError(maxRetries + 1, lastError);
}

/**
 * Convenience function for exponential backoff with predefined common options
 */
export const exponentialBackoffPresets = {
  /**
   * Quick retries for fast operations (3 retries, 500ms base delay)
   */
  quick: <T>(fn: () => Promise<T>) => exponentialBackoff(fn, { maxRetries: 3, baseDelay: 500 }),

  /**
   * Standard retries for most operations (3 retries, 1s base delay)
   */
  standard: <T>(fn: () => Promise<T>) => exponentialBackoff(fn, { maxRetries: 3, baseDelay: 1000 }),

  /**
   * Patient retries for slow operations (5 retries, 2s base delay)
   */
  patient: <T>(fn: () => Promise<T>) => exponentialBackoff(fn, { maxRetries: 5, baseDelay: 2000 }),

  /**
   * Aggressive retries for critical operations (7 retries, 1s base delay)
   */
  aggressive: <T>(fn: () => Promise<T>) => exponentialBackoff(fn, { maxRetries: 7, baseDelay: 1000 }),
} as const;
